import { useMemo } from 'react';
import { KeyboardSensor, PointerSensor, sortableKeyboardCoordinates, useSensor, useSensors } from '../adapters/dndAdapter';

/**
 * Interactive elements that should not trigger drag
 * @see https://github.com/clauderic/dnd-kit/issues/477
 */
const INTERACTIVE_ELEMENTS = ['button', 'input', 'textarea', 'select', 'option', 'a'];

/**
 * Checks if drag should be prevented for an element
 * Based on community patterns from @dnd-kit issues #477, #827
 *
 * @param {HTMLElement} element - The event target element
 * @returns {boolean} - True if drag should be prevented
 */
function shouldPreventDrag(element) {
  if (!element) return false;

  let current = element;
  while (current) {
    // 1. Check for data-no-dnd attribute (community standard pattern) — always wins
    if (current.dataset?.noDnd) {
      return true;
    }

    // 2. Check for data-dnd-zone — marks an intentional DnD context boundary
    // If found before a dialog/modal, allow drag (e.g. DnD inside a dialog)
    if (current.dataset?.dndZone !== undefined) {
      return false;
    }

    // 3. Check for MUI Dialog/Modal classes
    if (current.classList?.contains('MuiDialog-root') || current.classList?.contains('MuiModal-root')) {
      return true;
    }

    // 4. Check for role="dialog" or role="presentation" (accessibility)
    const role = current.getAttribute?.('role');
    if (role === 'dialog' || role === 'presentation') {
      return true;
    }

    // 5. Check for interactive elements (only at the target level)
    if (current === element && INTERACTIVE_ELEMENTS.includes(current.tagName?.toLowerCase())) {
      return true;
    }

    current = current.parentElement;
  }
  return false;
}

/**
 * Checks if keyboard drag should be prevented for an element
 * More lenient than shouldPreventDrag - only prevents inside dialogs/modales,
 * not for interactive elements (buttons, inputs) since keyboard users need to
 * activate drag from the draggable element itself, not its children
 *
 * @param {HTMLElement} element - The event target element
 * @returns {boolean} - True if keyboard drag should be prevented
 */
function shouldPreventKeyboardDrag(element) {
  if (!element) return false;

  let current = element;
  while (current) {
    // 1. Check for data-no-dnd attribute — always wins
    if (current.dataset?.noDnd) {
      return true;
    }

    // 2. Check for data-dnd-zone — marks an intentional DnD context boundary
    if (current.dataset?.dndZone !== undefined) {
      return false;
    }

    // 2. Check for MUI Dialog/Modal classes
    if (current.classList?.contains('MuiDialog-root') || current.classList?.contains('MuiModal-root')) {
      return true;
    }

    // 3. Check for role="dialog" or role="presentation"
    const role = current.getAttribute?.('role');
    if (role === 'dialog' || role === 'presentation') {
      return true;
    }

    // NOTE: We don't check for interactive elements here because keyboard users
    // Tab to the draggable element and press Space to activate drag. The element
    // may contain buttons/inputs, but @dnd-kit will only activate drag if the
    // listeners are on the focused element.

    current = current.parentElement;
  }
  return false;
}

/**
 * Custom PointerSensor that ignores events inside dialogs/modals and interactive elements
 * This follows the recommended pattern from @dnd-kit community
 * @see https://github.com/clauderic/dnd-kit/issues/477
 * @see https://github.com/clauderic/dnd-kit/issues/827
 */
class SmartPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }, { onActivation }) => {
        // Prevent drag for dialogs, modals, and interactive elements
        if (shouldPreventDrag(event.target)) {
          return false;
        }

        // Default PointerSensor behavior: check for primary button
        if (event.isPrimary && event.button === 0) {
          onActivation?.({ event });
          return true;
        }
        return false;
      },
    },
  ];
}

/**
 * Custom KeyboardSensor that ignores events inside dialogs/modals
 * Solves TAN-5342: Prevents keyboard sensor from interfering with text inputs in dialogs
 * while maintaining accessibility for keyboard-based drag-and-drop
 *
 * Uses shouldPreventKeyboardDrag instead of shouldPreventDrag to allow Space key
 * activation even when focus is within draggable elements containing buttons/inputs.
 * @dnd-kit will only activate drag if the element with listeners has focus.
 * @see https://github.com/clauderic/dnd-kit/issues/477
 */
class SmartKeyboardSensor extends KeyboardSensor {
  static activators = [
    {
      eventName: 'onKeyDown',
      handler: ({ nativeEvent: event }, { onActivation }) => {
        // Prevent keyboard drag activation only inside dialogs/modals
        // (not for interactive elements like buttons - let @dnd-kit handle that)
        if (shouldPreventKeyboardDrag(event.target)) {
          return false;
        }

        // Default KeyboardSensor behavior: Space key activates drag
        if (event.code === 'Space') {
          // Prevent event propagation to avoid triggering buttons/actions
          // inside the draggable element
          event.preventDefault();
          event.stopPropagation();
          onActivation?.({ event });
          return true;
        }
        return false;
      },
    },
  ];
}

const POINTER_DEFAULTS = {
  activationConstraint: {
    distance: 8, // Mínimo de píxeles para activar el drag
  },
};

const KEYBOARD_DEFAULTS = { coordinateGetter: sortableKeyboardCoordinates };

/**
 * Hook que configura los sensores por defecto para drag and drop
 * Incluye SmartPointerSensor para mouse/touch y SmartKeyboardSensor para teclado
 * Ambos con protección contra dialogs/modales para evitar conflictos con inputs
 *
 * Soluciona TAN-5342: Los sensores filtran eventos dentro de dialogs, permitiendo
 * que inputs de teclado funcionen correctamente mientras mantienen accesibilidad
 * de drag-and-drop para usuarios que dependen de navegación por teclado.
 *
 * Opciones memoizadas: @dnd-kit memoiza cada sensor por la identidad de sus opciones, y un objeto nuevo en cada render
 * cambia los listeners de todos los items y rompe su memoización.
 *
 * @param {Object} options - Opciones de configuración
 * @param {Object} options.pointerOptions - Opciones para SmartPointerSensor
 * @param {Object} options.keyboardOptions - Opciones para SmartKeyboardSensor
 * @returns {Array} Array de sensores configurados
 */
function useDndSensors({ pointerOptions, keyboardOptions } = {}) {
  const pointer = useMemo(() => (pointerOptions ? { ...POINTER_DEFAULTS, ...pointerOptions } : POINTER_DEFAULTS), [pointerOptions]);
  const keyboard = useMemo(() => (keyboardOptions ? { ...KEYBOARD_DEFAULTS, ...keyboardOptions } : KEYBOARD_DEFAULTS), [keyboardOptions]);

  return useSensors(useSensor(SmartPointerSensor, pointer), useSensor(SmartKeyboardSensor, keyboard));
}

export default useDndSensors;
